import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Minus, Plus } from "lucide-react";
import { Brief } from "./data";
import { CITY_DOTS, UKMap, money } from "./shared";
import { cn } from "@/lib/utils";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const CITY_OPTIONS = Object.keys(CITY_DOTS);

/** An inline, underlined token inside the brief sentence. */
function Token({
  children,
  open,
  onToggle,
  panel,
}: {
  children: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  panel: React.ReactNode;
}) {
  return (
    <span className="relative inline-block align-baseline">
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "font-funnel font-bold rounded-lg px-1.5 -mx-0.5 transition-colors",
          "underline decoration-primary/30 decoration-2 underline-offset-[6px]",
          open
            ? "bg-primary/10 decoration-primary"
            : "hover:bg-primary/5 hover:decoration-primary/60"
        )}
      >
        {children}
      </button>

      <AnimatePresence>
        {open && (
          <motion.span
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.16 }}
            className="absolute left-0 top-[calc(100%+12px)] z-30 block w-[min(20rem,80vw)] rounded-2xl border border-border bg-card p-4 text-base font-normal font-sans shadow-sm"
          >
            {panel}
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}

function Stepper({
  value,
  onChange,
  min = 1,
  max = 200,
  step = 1,
  label,
}: {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label: string;
}) {
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-3">{label}</p>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - step))}
          className="h-9 w-9 shrink-0 rounded-full border border-border flex items-center justify-center text-foreground hover:border-primary/60 transition-colors"
          aria-label="decrease"
        >
          <Minus size={14} />
        </button>
        <span className="font-funnel font-bold text-2xl text-foreground min-w-[3ch] text-center">
          {value}
        </span>
        <button
          type="button"
          onClick={() => onChange(Math.min(max, value + step))}
          className="h-9 w-9 shrink-0 rounded-full border border-border flex items-center justify-center text-foreground hover:border-primary/60 transition-colors"
          aria-label="increase"
        >
          <Plus size={14} />
        </button>
      </div>
    </div>
  );
}

export default function BriefSentence({
  brief,
  setBrief,
  onPlan,
  thinking,
}: {
  brief: Brief;
  setBrief: (b: Brief) => void;
  onPlan: () => void;
  thinking: boolean;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const toggle = (id: string) => setOpen((o) => (o === id ? null : id));
  const sentenceRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!sentenceRef.current?.contains(e.target as Node)) setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(null);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const placed = brief.cities.filter((c) => c.name && c.count > 0);
  const placedTotal = placed.reduce((s, c) => s + c.count, 0);

  const cityLabel = useMemo(() => {
    if (!placed.length) return "nowhere yet";
    if (placed.length <= 2) return placed.map((c) => c.name).join(" and ");
    return `${placed[0].name}, ${placed[1].name} and ${placed.length - 2} more`;
  }, [placed]);

  const balanceLabel =
    brief.balance >= 70
      ? "properly productive"
      : brief.balance >= 55
      ? "work-led"
      : brief.balance >= 45
      ? "an even split"
      : brief.balance >= 30
      ? "social-led"
      : "mostly social";

  const setCityCount = (name: string, next: number) => {
    const others = brief.cities.filter((c) => c.name !== name);
    const list = next > 0 ? [...others, { name, count: next }] : others;
    const total = list.reduce((s, c) => s + c.count, 0);
    setBrief({ ...brief, cities: list, teamSize: Math.max(total, brief.teamSize) });
  };

  return (
    <section id="brief" className="relative min-h-[100svh] flex flex-col scroll-mt-0">
      <div className="container mx-auto px-4 laptop:px-6 desktop:px-6 flex-1 flex flex-col justify-center py-20">
        <div className="grid mobile:grid-cols-1 tablet:grid-cols-1 laptop:grid-cols-[1fr_320px] desktop:grid-cols-[1fr_360px] gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-8">
              qwip offsites · internal concept
            </p>

            <h1
              ref={sentenceRef}
              className="font-sans text-foreground leading-[1.5] text-xl tablet:text-2xl laptop:text-[1.9rem] desktop:text-[1.9rem]"
            >
              we are{" "}
              <Token
                open={open === "size"}
                onToggle={() => toggle("size")}
                panel={
                  <Stepper
                    label="how many people are coming?"
                    value={brief.teamSize}
                    min={Math.max(2, placedTotal)}
                    onChange={(n) => setBrief({ ...brief, teamSize: n })}
                  />
                }
              >
                {brief.teamSize} people
              </Token>{" "}
              spread across{" "}
              <Token
                open={open === "cities"}
                onToggle={() => toggle("cities")}
                panel={
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">
                      where does everyone travel from?
                    </p>
                    <div className="max-h-64 overflow-y-auto pr-1 space-y-1">
                      {CITY_OPTIONS.map((city) => {
                        const found = brief.cities.find((c) => c.name === city);
                        const count = found?.count ?? 0;
                        return (
                          <div
                            key={city}
                            className={cn(
                              "flex items-center justify-between gap-3 rounded-xl px-3 py-1.5 transition-colors",
                              count > 0 ? "bg-primary/10" : "hover:bg-muted"
                            )}
                          >
                            <span className="text-sm text-foreground">{city}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                type="button"
                                onClick={() => setCityCount(city, Math.max(0, count - 1))}
                                className="h-6 w-6 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground"
                                aria-label={`fewer from ${city}`}
                              >
                                <Minus size={11} />
                              </button>
                              <span className="w-4 text-center text-sm text-foreground">{count}</span>
                              <button
                                type="button"
                                onClick={() => setCityCount(city, count + 1)}
                                className="h-6 w-6 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground"
                                aria-label={`more from ${city}`}
                              >
                                <Plus size={11} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                }
              >
                {cityLabel}
              </Token>
              , and we want{" "}
              <Token
                open={open === "format"}
                onToggle={() => toggle("format")}
                panel={
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">how long is it?</p>
                    <div className="flex flex-wrap gap-2">
                      {(
                        [
                          ["day", "one full day"],
                          ["overnight", "one night away"],
                        ] as const
                      ).map(([id, label]) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setBrief({ ...brief, format: id })}
                          className={cn(
                            "rounded-full border px-4 py-2 text-sm transition-colors",
                            brief.format === id
                              ? "bg-primary text-primary-foreground border-primary"
                              : "border-border text-foreground hover:border-primary/60"
                          )}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                }
              >
                {brief.format === "day" ? "one full day" : "one night away"}
              </Token>{" "}
              in{" "}
              <Token
                open={open === "when"}
                onToggle={() => toggle("when")}
                panel={
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">roughly when?</p>
                    <div className="flex flex-wrap gap-1.5">
                      {MONTHS.map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => {
                            setBrief({ ...brief, dateMode: "flexible-month", dateValue: `a ${m} weekday` });
                            setOpen(null);
                          }}
                          className={cn(
                            "rounded-full border px-3 py-1.5 text-xs transition-colors",
                            brief.dateValue.includes(m)
                              ? "bg-primary text-primary-foreground border-primary"
                              : "border-border text-foreground hover:border-primary/60"
                          )}
                        >
                          {m.toLowerCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                }
              >
                {brief.dateValue || "sometime soon"}
              </Token>
              . we have{" "}
              <Token
                open={open === "budget"}
                onToggle={() => toggle("budget")}
                panel={
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">
                      total budget · {money(brief.budgetTotal)} ·{" "}
                      {money(brief.budgetTotal / Math.max(1, brief.teamSize))} per person
                    </p>
                    <input
                      type="range"
                      min={500}
                      max={20000}
                      step={100}
                      value={brief.budgetTotal}
                      onChange={(e) => setBrief({ ...brief, budgetTotal: Number(e.target.value) })}
                      className="slider-thumb w-full appearance-none h-1.5 rounded-full bg-muted accent-primary"
                    />
                  </div>
                }
              >
                {money(brief.budgetTotal)}
              </Token>{" "}
              to spend, and the day should be{" "}
              <Token
                open={open === "balance"}
                onToggle={() => toggle("balance")}
                panel={
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {brief.balance} productive / {100 - brief.balance} social
                    </p>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={brief.balance}
                      onChange={(e) => setBrief({ ...brief, balance: Number(e.target.value) })}
                      className="slider-thumb w-full appearance-none h-1.5 rounded-full bg-muted accent-primary"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-2">
                      <span>all social</span>
                      <span>all work</span>
                    </div>
                  </div>
                }
              >
                {balanceLabel}
              </Token>
              <span className="font-cormorant italic">.</span>
            </h1>

            <div className="mt-12 flex flex-wrap items-center gap-5">
              <button
                onClick={onPlan}
                disabled={thinking}
                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3.5 text-base hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {thinking ? "building three plans..." : "what do you have for me"}
                {!thinking && <ArrowRight size={16} />}
              </button>
              <p className="text-sm text-muted-foreground max-w-xs">
                edit any underlined part of the sentence, or just ask.
              </p>
            </div>

          </div>

          <div className="mobile:hidden tablet:hidden">
            <div className="rounded-2xl border border-border bg-card p-6">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">
                your team
              </p>
              <div className="aspect-[3/4]">
                <UKMap dots={placed} />
              </div>
              <div className="mt-4 space-y-1.5">
                {placed.length ? (
                  placed.map((c) => (
                    <div key={c.name} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{c.name}</span>
                      <span className="text-foreground">{c.count}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">add a city to see your spread</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
}
